import i18next from 'i18next';
import { toast } from 'sonner';
import { usePrivacyNoticeStore } from '@/store/privacy-notice.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class PrivacyNoticeIntegration {
    constructor(sdkService, personalDataInventoryService = null) {
        this.service = sdkService;
        this.personalDataInventoryService = personalDataInventoryService;
    }

    async fetchItems(params = {}) {
        const store = usePrivacyNoticeStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        if (store.clearError) store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const page = params.page || store.paginationData?.currentPage || 1;
            const pageSize = params.pageSize || store.paginationData?.itemsPerPage || 10;
            const search = params.search ?? '';
            const sortBy = params.sortBy || 'createdAt';
            const sortOrder = params.sortOrder || 'desc';
            const columnFilters = params.columnFilters ?? store.columnFilters ?? {};

            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value?.length) acc[`filter_${key}`] = value;
                    return acc;
                }, {}),
            });

            store.setItems(response.data || []);
            if (response.pagination) {
                store.setPaginationData({
                    currentPage: response.pagination.page,
                    totalPages: response.pagination.totalPages,
                    totalItems: response.pagination.total,
                    itemsPerPage: response.pagination.pageSize,
                });
            }
            if (response.uniqueValues) store.setUniqueValues(response.uniqueValues);
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async createItem(data) {
        const store = usePrivacyNoticeStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(
                i18next.t('gdprPrivacyNotice:messages.createSuccess', { defaultValue: 'Privacy notice created' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async updateItem(id, data) {
        const store = usePrivacyNoticeStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, data);
            toast.success(
                i18next.t('gdprPrivacyNotice:messages.updateSuccess', { defaultValue: 'Privacy notice updated' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteItem(id) {
        const store = usePrivacyNoticeStore.getState();
        store.setLoading(true);
        try {
            await this.service.delete(id);
            toast.success(
                i18next.t('gdprPrivacyNotice:messages.deleteSuccess', { defaultValue: 'Privacy notice deleted' }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async generateDraft(linkedInventoryItems) {
        try {
            const response = await this.service.generateDraft({ linkedInventoryItems });
            toast.success(
                i18next.t('gdprPrivacyNotice:messages.draftGenerated', { defaultValue: 'Draft text generated from inventory' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchChecklist(id) {
        const store = usePrivacyNoticeStore.getState();
        store.setChecklistLoading(true);
        try {
            const response = await this.service.getChecklist(id);
            store.setChecklist(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setChecklistLoading(false);
        }
    }

    async submitItem(id) {
        try {
            const response = await this.service.submit(id);
            toast.success(
                i18next.t('gdprPrivacyNotice:messages.submitSuccess', { defaultValue: 'Notice submitted for approval' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async publishItem(id) {
        try {
            const response = await this.service.publish(id);
            toast.success(
                i18next.t('gdprPrivacyNotice:messages.publishSuccess', { defaultValue: 'Notice published' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async createNewVersion(id) {
        try {
            const response = await this.service.newVersion(id);
            toast.success(
                i18next.t('gdprPrivacyNotice:messages.newVersionSuccess', { defaultValue: 'New draft version created' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /** Option list for the inventory-item selector — no store writes. */
    async fetchActivityOptions(search = '') {
        if (!this.personalDataInventoryService) return { success: false, error: 'Service unavailable' };
        try {
            const response = await this.personalDataInventoryService.getAll({
                page: 1,
                pageSize: 200,
                search,
                sortBy: 'activity',
                sortOrder: 'asc',
            });
            return { success: true, data: response.data || [] };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
